import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Home, ArrowLeft, Trash2, UserPlus, Shield, Activity, Mail, Copy, Clock, Send, RotateCcw, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { EmployeeList } from '@/components/employees/EmployeeList';
import { InviteDialog } from '@/components/employees/InviteDialog';

const formatTimeLeft = (iso) => {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
};

export default function EmployeesPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [invites, setInvites] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showActivity, setShowActivity] = useState(false);
  const [qrInvite, setQrInvite] = useState(null);

  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    permissions: [],
    active: true,
    password: '',
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, invRes, permRes, logRes] = await Promise.all([
        axios.get(`${API}/employees`),
        axios.get(`${API}/employee-invites`),
        axios.get(`${API}/permissions`),
        axios.get(`${API}/activity-log`),
      ]);
      setEmployees(empRes.data);
      setInvites(invRes.data);
      setPermissions(permRes.data);
      setActivityLog(logRes.data);
    } catch (e) {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'landlord') {
      navigate('/');
      return;
    }
    fetchAll();
  }, [user, navigate, fetchAll]);

  const pendingInvites = useMemo(
    () => invites.filter((i) => i.status === 'pending'),
    [invites]
  );
  const pastInvites = useMemo(
    () => invites.filter((i) => i.status !== 'pending' && i.status !== 'accepted'),
    [invites]
  );

  const buildInviteLink = (inviteToken) => `${window.location.origin}/invite/accept?token=${inviteToken}`;

  const copyLink = (link) => {
    navigator.clipboard.writeText(link).then(
      () => toast.success('Invite link copied to clipboard!'),
      () => toast.error('Could not copy — long-press to copy manually')
    );
  };

  const toggleEditPermission = (key) => {
    setEditForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key],
    }));
  };

  const revokeInvite = async (inv) => {
    if (!window.confirm(`Revoke the invite for ${inv.email}?`)) return;
    try {
      await axios.delete(`${API}/employee-invites/${inv.id}`);
      toast.success('Invite revoked');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke');
    }
  };

  const resendInvite = async (inv) => {
    // Create a fresh invite with the same details (the old one gets auto-revoked by the backend on new invite for same email)
    try {
      const res = await axios.post(
        `${API}/employee-invites`,
        { email: inv.email, name: inv.name, permissions: inv.permissions || [] },
      );
      const link = buildInviteLink(res.data.token);
      copyLink(link);
      toast.success('New invite created and link copied');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend');
    }
  };

  const openEditEmployee = (emp) => {
    setEditingEmployee(emp);
    setEditForm({
      name: emp.name,
      phone: emp.phone || '',
      permissions: emp.permissions || [],
      active: emp.active !== false,
      password: '',
    });
    setShowEditDialog(true);
  };

  const submitEditEmployee = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: editForm.name,
        phone: editForm.phone || null,
        permissions: editForm.permissions,
        active: editForm.active,
      };
      if (editForm.password) payload.password = editForm.password;
      await axios.patch(`${API}/employees/${editingEmployee.id}`, payload);
      toast.success('Employee updated');
      setShowEditDialog(false);
      setEditingEmployee(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update employee');
    }
  };

  const deleteEmployee = async (id, name) => {
    if (!window.confirm(`Remove ${name} from your team?`)) return;
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success('Employee removed');
      fetchAll();
    } catch {
      toast.error('Failed to remove employee');
    }
  };

  const statusBadge = (status) => {
    const base = 'text-xs px-2 py-0.5 rounded-full font-medium';
    if (status === 'pending') return <span className={`${base} bg-amber-100 text-amber-800`}>Pending</span>;
    if (status === 'accepted') return <span className={`${base} bg-green-100 text-green-800`}>Accepted</span>;
    if (status === 'expired') return <span className={`${base} bg-muted text-muted-foreground`}>Expired</span>;
    if (status === 'revoked') return <span className={`${base} bg-red-100 text-red-700`}>Revoked</span>;
    return <span className={`${base} bg-muted text-muted-foreground`}>{status}</span>;
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header bg-white/70 border-b border-border/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/landlord/dashboard')}>
            <Home className="w-7 h-7 text-primary" />
            <span className="text-2xl font-bold tracking-tight text-primary">PrimePocket</span>
          </div>
          <Button data-testid="back-to-dashboard-btn" variant="ghost" onClick={() => navigate('/landlord/dashboard')} className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-primary">Team Members</h1>
            <p className="text-muted-foreground mt-2">Invite property managers and assistants to join your team.</p>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="toggle-activity-log-btn"
              variant="outline"
              onClick={() => setShowActivity((s) => !s)}
              className="rounded-full"
            >
              <Activity className="w-4 h-4 mr-2" />
              {showActivity ? 'Hide' : 'View'} Activity Log
            </Button>
            <Button
              data-testid="invite-employee-btn"
              onClick={() => { setShowInviteDialog(true); }}
              className="rounded-full bg-primary hover:bg-primary/90"
            >
              <UserPlus className="w-4 h-4 mr-2" /> Invite Employee
            </Button>
          </div>
        </div>

        {showActivity && (
          <Card className="p-6 mb-8" data-testid="activity-log-panel">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-semibold">Team Activity Log</h2>
            </div>
            {activityLog.length === 0 ? (
              <p className="text-muted-foreground text-sm">No activity recorded yet.</p>
            ) : (
              <ul className="space-y-3 max-h-96 overflow-y-auto">
                {activityLog.map((log) => (
                  <li key={log.id} className="flex items-start gap-3 pb-3 border-b border-border/40 last:border-0" data-testid={`activity-log-item-${log.id}`}>
                    <div className="mt-1 w-2 h-2 rounded-full bg-primary/70" />
                    <div className="flex-1">
                      <p className="text-sm">
                        <span className="font-semibold">{log.actor_name}</span>{' '}
                        <span className="text-muted-foreground">{log.action}</span>{' '}
                        {log.details && <span className="text-foreground/80">— {log.details}</span>}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {new Date(log.created_at).toLocaleString()}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        {/* Pending Invites */}
        {pendingInvites.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" /> Pending Invites
            </h2>
            <div className="grid gap-3">
              {pendingInvites.map((inv) => {
                const link = buildInviteLink(inv.token);
                return (
                  <Card key={inv.id} className="p-4" data-testid={`invite-card-${inv.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{inv.name}</span>
                          {statusBadge(inv.status)}
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {formatTimeLeft(inv.expires_at)}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" /> {inv.email}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {(inv.permissions || []).map((p) => (
                            <span key={p} className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                              {permissions.find((mp) => mp.key === p)?.label || p}
                            </span>
                          ))}
                        </div>
                        <div className="mt-2 flex items-center gap-2 bg-muted/40 rounded-md px-3 py-1.5">
                          <span className="text-xs text-muted-foreground truncate flex-1 font-mono" title={link}>{link}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button data-testid={`show-qr-invite-${inv.id}`} variant="outline" size="sm" onClick={() => setQrInvite({ link, name: inv.name, email: inv.email })}>
                          <QrCode className="w-3.5 h-3.5 mr-1" /> QR
                        </Button>
                        <Button data-testid={`copy-invite-link-${inv.id}`} variant="outline" size="sm" onClick={() => copyLink(link)}>
                          <Copy className="w-3.5 h-3.5 mr-1" /> Copy Link
                        </Button>
                        <Button data-testid={`resend-invite-${inv.id}`} variant="outline" size="sm" onClick={() => resendInvite(inv)}>
                          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Refresh
                        </Button>
                        <Button data-testid={`revoke-invite-${inv.id}`} variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => revokeInvite(inv)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Past invites (expired/revoked) — collapsed */}
        {pastInvites.length > 0 && (
          <details className="mb-8" data-testid="past-invites-toggle">
            <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
              Show past invites ({pastInvites.length})
            </summary>
            <div className="grid gap-2 mt-3">
              {pastInvites.map((inv) => (
                <div key={inv.id} className="text-sm px-4 py-2 rounded-md bg-muted/30 flex items-center justify-between" data-testid={`past-invite-${inv.id}`}>
                  <span>{inv.name} — {inv.email}</span>
                  {statusBadge(inv.status)}
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Active Employees */}
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" /> Team ({employees.length})
        </h2>
        <EmployeeList
          employees={employees}
          permissions={permissions}
          loading={loading}
          onEdit={openEditEmployee}
          onDelete={deleteEmployee}
          onInvite={() => setShowInviteDialog(true)}
        />
      </div>

      {/* Invite Dialog — extracted, self-contained */}
      <InviteDialog
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
        permissions={permissions}
        onInviteCreated={fetchAll}
      />

      {/* QR Viewer Dialog */}
      <Dialog open={!!qrInvite} onOpenChange={(v) => { if (!v) setQrInvite(null); }}>
        <DialogContent className="max-w-sm" data-testid="qr-viewer-dialog">
          <DialogHeader>
            <DialogTitle>Scan to Accept Invite</DialogTitle>
          </DialogHeader>
          {qrInvite && (
            <div className="space-y-4">
              <div className="text-sm text-center text-muted-foreground">
                Have <span className="font-semibold text-foreground">{qrInvite.name}</span> scan this with their phone camera.
              </div>
              <div className="flex justify-center">
                <div className="p-4 bg-white rounded-xl border-2 border-primary/20">
                  <QRCodeSVG value={qrInvite.link} size={220} bgColor="#ffffff" fgColor="#1A3622" level="M" />
                </div>
              </div>
              <div className="text-xs text-muted-foreground text-center break-all font-mono px-4">
                {qrInvite.email}
              </div>
              <Button data-testid="qr-copy-link-btn" onClick={() => copyLink(qrInvite.link)} className="w-full rounded-full bg-primary hover:bg-primary/90">
                <Copy className="w-4 h-4 mr-2" /> Copy Link Instead
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Employee Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(v) => { setShowEditDialog(v); if (!v) setEditingEmployee(null); }}>
        <DialogContent className="max-w-lg" data-testid="edit-employee-dialog">
          <DialogHeader>
            <DialogTitle>Edit Employee</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitEditEmployee} className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input
                data-testid="employee-name-input"
                required
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Phone (optional)</Label>
              <Input
                data-testid="employee-phone-input"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              />
            </div>
            <div>
              <Label>Reset Password (leave blank to keep)</Label>
              <Input
                data-testid="employee-password-input"
                type="password"
                minLength={6}
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
              />
            </div>
            <div>
              <Label className="mb-2 block">Permissions</Label>
              <div className="space-y-2 border rounded-lg p-3 bg-muted/30">
                {permissions.map((perm) => (
                  <label key={perm.key} className="flex items-start gap-3 cursor-pointer hover:bg-white/60 p-2 rounded-md">
                    <Checkbox
                      data-testid={`perm-checkbox-${perm.key}`}
                      checked={editForm.permissions.includes(perm.key)}
                      onCheckedChange={() => toggleEditPermission(perm.key)}
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{perm.label}</p>
                      <p className="text-xs text-muted-foreground">{perm.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="text-sm font-medium">Account Active</p>
                <p className="text-xs text-muted-foreground">Disable to revoke login access</p>
              </div>
              <Switch
                data-testid="employee-active-switch"
                checked={editForm.active}
                onCheckedChange={(v) => setEditForm({ ...editForm, active: v })}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-employee-btn" className="bg-primary hover:bg-primary/90">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
